import React, { useState, useEffect } from 'react';
import { KREDITTKORT, MAANEDER, AAR, formatNOK } from './data';

// Stiltokens — speiler C og mono i App.js
const C = {
  surface: '#FFFFFF', surface2: '#E8ECF0', border: '#CBD2DA',
  text: '#1A2332', muted: '#6B7888',
  green: '#2D7D9A', greenL: '#E3F2F8', red: '#C0392B', redL: '#FBEAE8',
  blue: '#2C5F8A', blueL: '#E3EDF6', amber: '#7D6608', amberL: '#FEF9E7',
  dark: '#1A2332',
};
const mono = { fontFamily: "'DM Mono', monospace" };

// ══════════════════════════════════════════════════════════════════════════
// KATEGORI-TRANSAKSJONER — modal som viser alle underliggende transaksjoner
// for en kategori, gruppert per måned. Kilde: pushTransaksjoner for KK-
// kategorier, lagrede banktransaksjoner (siste synk) for resten.
// ══════════════════════════════════════════════════════════════════════════
export default function KategoriTransaksjoner({ token, kategori, onLukk }) {
  const [laster, setLaster] = useState(true);
  const [grupper, setGrupper] = useState([]);
  const [feil, setFeil] = useState(null);
  const [kilde, setKilde] = useState('');

  useEffect(() => {
    let avbrutt = false;
    (async () => {
      setLaster(true); setFeil(null); setGrupper([]);
      try {
        const erKK = kategori in KREDITTKORT;
        let rader = [];
        if (erKK) {
          const d = await fetch(`/api/push/transaksjoner?aar=${AAR}&kategori=${encodeURIComponent(kategori)}`, { headers: { 'x-token': token } }).then(r => r.json());
          if (Array.isArray(d)) {
            rader = d.map(t => ({ dato: (t.opprettet || '').slice(0, 10), tekst: t.butikk, beloep: t.beloep, maaned: t.maaned, aar: t.aar }));
            if (!avbrutt) setKilde('KK-transaksjoner (push + Excel-import)');
          } else if (!avbrutt) setFeil(d.error || 'Kunne ikke hente transaksjoner');
        } else {
          const d = await fetch('/api/bank/lagrede', { headers: { 'x-token': token } }).then(r => r.json());
          if (d.ok && Array.isArray(d.transaksjoner)) {
            rader = d.transaksjoner
              .filter(t => t.kategori === kategori)
              .map(t => {
                const dt = new Date(t.dato);
                return { dato: t.dato, tekst: t.beskrivelse, beloep: t.beloep, maaned: dt.getMonth() + 1, aar: dt.getFullYear() };
              });
            if (!avbrutt) setKilde(`bankkonto — siste synk ${d.hentet ? new Date(d.hentet).toLocaleDateString('no-NO') : 'ukjent'}`);
          } else if (!avbrutt) setFeil(d.error || 'Ingen lagrede banktransaksjoner — kjør en synk i Bank-fanen først');
        }
        rader = rader.filter(r => r.aar === AAR);
        const perMnd = {};
        for (const r of rader) (perMnd[r.maaned] = perMnd[r.maaned] || []).push(r);
        const g = Object.keys(perMnd).map(Number).sort((a, b) => b - a).map(m => ({
          maaned: m,
          sum: perMnd[m].reduce((s, r) => s + r.beloep, 0),
          rader: [...perMnd[m]].sort((a, b) => (b.dato || '').localeCompare(a.dato || '')),
        }));
        if (!avbrutt) setGrupper(g);
      } catch { if (!avbrutt) setFeil('Kunne ikke hente transaksjoner'); }
      if (!avbrutt) setLaster(false);
    })();
    return () => { avbrutt = true; };
  }, [kategori, token]);

  return (
    <div onClick={onLukk} style={{ position: 'fixed', inset: 0, background: 'rgba(26,35,50,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{kategori}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Transaksjoner {AAR}{kilde ? ` · ${kilde}` : ''}</div>
          </div>
          <button onClick={onLukk} style={{ background: C.surface2, border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: C.text, flexShrink: 0 }}>✕ Lukk</button>
        </div>

        {/* Innhold */}
        <div style={{ overflowY: 'auto', padding: '12px 18px' }}>
          {laster && <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>Laster…</div>}
          {feil && <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 12, background: C.redL, color: C.red }}>{feil}</div>}
          {!laster && !feil && grupper.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>Ingen transaksjoner funnet for {kategori} i {AAR}.</div>
          )}
          {grupper.map(g => (
            <div key={g.maaned} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: C.surface2, borderRadius: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{MAANEDER[g.maaned - 1]} {AAR}</span>
                <span style={{ ...mono, fontSize: 12, fontWeight: 700 }}>{formatNOK(g.sum)}</span>
              </div>
              {g.rader.map((r, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 90px', gap: 8, alignItems: 'center', padding: '6px 8px', borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                  <span style={{ ...mono, color: C.muted, fontSize: 11 }}>{(r.dato || '').slice(5).replace('-', '.')}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.tekst}</span>
                  <span style={{ ...mono, textAlign: 'right' }}>{formatNOK(r.beloep)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
